import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import { Layout } from './components';
import { config } from './config';
import { routeConfigs, RouteConfig } from './config/routes.config';
import { useDocumentTitle } from './core';
import { useUserPermissions } from './core/hooks/useUserPermissions';
import './index.css';
import { DropdownMenu } from './modules/layouts/DropdownMenu';
import { useGetTitle } from './modules/layouts/sidebar/hooks/useGetTitle';
// import { MenuItemList } from './modules/menu-items/UI/List';
import { Sidebar } from './components/Sidebar';
import { AppProvider } from './core/context/AppContext';
import { UserLogin } from './modules/users/screens';
import { UserForgotPassword } from './modules/users/screens/forgot-password';
import { UserResetPassword } from './modules/users/screens';
import { Logout } from './components/Logout';
import { Homepage } from './modules/homepage';
import { ChangePasswordPage } from './modules/profile/screens/ChangePasswordPage';
import { ProfilePage } from './modules/profile/screens/ProfilePage';
const { Header, Content, Footer } = Layout;

const AuthenticatedLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const { title, setTitle, hanldeTittle } = useGetTitle();
  const location = useLocation();
  const pathName = location.pathname;
  const path = pathName.slice(1);
  const { hasPermission } = useUserPermissions();

  useEffect(() => {
    hanldeTittle(path);
  }, [pathName]);

  // Get all accessible routes based on permissions
  const getAccessibleRoutes = (): JSX.Element[] => {
    const accessibleRoutes: JSX.Element[] = [];

    const processRoute = (route: RouteConfig) => {
      if (hasPermission(route.permission)) {
        const Component = route.component;
        if (Component) {
          accessibleRoutes.push(
            <Route key={route.path} path={route.path} element={<Component />} />,
          );
        }
      }

      // Process children routes
      if (route.children) {
        route.children.forEach(processRoute);
      }
    };

    routeConfigs.forEach(processRoute);
    return accessibleRoutes;
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} setTitle={setTitle} />
      <Layout className='site-layout'>
        <Header className='site-layout-background px-3 flex items-center'>
          {React.createElement(collapsed ? RightOutlined : LeftOutlined, {
            onClick: () => setCollapsed(!collapsed),
          })}
          <Typography.Title className='ml-2 mt-2' level={4}>
            {title}
          </Typography.Title>
          <DropdownMenu />
        </Header>
        <Content style={{ margin: '24px 16px', height: '100%' }}>
          <Routes>
            {/* <Route path='/menu-items' element={<MenuItemList />} /> */}
            <Route path='/' element={<Homepage />} />
            {/*
              Current user profile - bypasses permission system because:
              1. Users can only access their own profile (/me is implicit self-reference)
              2. No permission granularity needed (view-only, no deletions/role changes)
              3. Authentication alone is sufficient (AppProvider gates all authenticated routes)
              This is a structural exception to the permission-gated pattern used elsewhere.
            */}
            <Route path='/me' element={<ProfilePage />} />
            <Route path='/change-password' element={<ChangePasswordPage />} />
            {getAccessibleRoutes()}
            <Route path='*' element={<span>Page not found</span>} />
          </Routes>
        </Content>
        <Footer style={{ textAlign: 'center' }}>{config.footer.text}</Footer>
      </Layout>
    </Layout>
  );
};

const App: React.FC = () => {
  useDocumentTitle();

  return (
    <Routes>
      {/* Public routes - no authentication required */}
      <Route path='/login' element={<UserLogin />} />
      <Route path='/forgot-password' element={<UserForgotPassword />} />
      <Route path='/reset-password' element={<UserResetPassword />} />
      <Route path='/logout' element={<Logout />} />
      {/* Protected routes - require authentication */}
      <Route
        path='*'
        element={
          <AppProvider>
            <AuthenticatedLayout />
          </AppProvider>
        }
      />
    </Routes>
  );
};

export default App;
